import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { brandOnDark, normalizeHex } from '../utilities/brandColors';

// Tema do portal: claro, escuro ou o do sistema (Windows, celular). A escolha fica neste navegador (localStorage) e o tema
// aplicado vai no data-bs-theme do <html>, que o Bootstrap 5.3 e o theme.css usam. O public/index.html faz o mesmo antes
// do React carregar, para a página não abrir branca no modo escuro: mudou a regra aqui, mude lá também
export const THEME_STORAGE_KEY = 'portal.theme';
export const THEME_MODES = ['light', 'dark', 'system'];
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const BRAND_FALLBACK = '#680e8f';
// Cor do menu (a marca): vem de Configurações > Menu, salva no banco e igual para todos. Enquanto ninguém salvou uma cor, vale a
// padrão: a do REACT_APP_PRIMARY_COLOR (.env) ou o roxo do Ajust. A última cor recebida fica neste navegador, para o menu já
// abrir com ela antes da resposta da API
export const DEFAULT_BRAND_COLOR = normalizeHex(process.env.REACT_APP_PRIMARY_COLOR) || BRAND_FALLBACK;
export const BRAND_STORAGE_KEY = 'portal.menuColor';

// Escolha salva; sem escolha (ou com o localStorage bloqueado), segue o sistema
export function readThemeMode() {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    return THEME_MODES.includes(saved) ? saved : 'system';
  } catch (error) {
    return 'system';
  }
}

// Cor do menu guardada neste navegador; sem cor (ou com o localStorage bloqueado), a padrão
export function readBrandColor() {
  try {
    return normalizeHex(window.localStorage.getItem(BRAND_STORAGE_KEY)) || DEFAULT_BRAND_COLOR;
  } catch (error) {
    return DEFAULT_BRAND_COLOR;
  }
}

function systemPrefersDark() {
  return Boolean(window.matchMedia && window.matchMedia(DARK_QUERY).matches);
}

// 'light' | 'dark' | 'system' -> 'light' | 'dark'
export function resolveTheme(mode, prefersDark = systemPrefersDark()) {
  if (mode === 'system') return prefersDark ? 'dark' : 'light';
  return mode === 'dark' ? 'dark' : 'light';
}

// Tema aplicado agora (para quem não é componente, como os alertas do SweetAlert)
export function currentTheme() {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light';
}

// Cor da marca para o CSS: --app-brand (preenchimento com texto branco, igual nos dois temas) e --app-brand-on-dark (texto,
// link e contorno no escuro). As duas saem da cor do menu
function applyBrandColors(brandColor) {
  const root = document.documentElement.style;
  root.setProperty('--app-brand', brandColor);
  root.setProperty('--app-brand-on-dark', brandOnDark(brandColor, BRAND_FALLBACK));
}

const ThemeContext = createContext({ mode: 'light', theme: 'light', setMode: () => {}, brandColor: DEFAULT_BRAND_COLOR, setBrandColor: () => {} });

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readThemeMode);
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);
  const [brandColor, setBrandColorState] = useState(readBrandColor);
  const theme = resolveTheme(mode, prefersDark);

  // Antes da pintura, para o menu e os links não piscarem na cor anterior
  useLayoutEffect(() => {
    applyBrandColors(brandColor);
  }, [brandColor]);

  // Antes da pintura, para a tela não piscar no tema anterior
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  // "Sistema": o Windows ou o celular trocou de tema com o portal aberto
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event) => setPrefersDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Outra aba do portal trocou o tema ou salvou outra cor do menu
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === THEME_STORAGE_KEY) setModeState(readThemeMode());
      if (event.key === BRAND_STORAGE_KEY) setBrandColorState(readBrandColor());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next) => {
    if (!THEME_MODES.includes(next)) return;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (error) {
      // localStorage bloqueado: o tema vale só até fechar a página
    }
    setModeState(next);
  }, []);

  // Cor do menu que veio da API (ao abrir o portal ou ao salvar em Configurações > Menu): vale na hora e fica no navegador.
  // Vazia (ninguém salvou) ou inválida: volta para a padrão
  const setBrandColor = useCallback((color) => {
    const next = normalizeHex(color);
    try {
      if (next) window.localStorage.setItem(BRAND_STORAGE_KEY, next);
      else window.localStorage.removeItem(BRAND_STORAGE_KEY);
    } catch (error) {
      // localStorage bloqueado: a cor vale só até fechar a página
    }
    setBrandColorState(next || DEFAULT_BRAND_COLOR);
  }, []);

  const value = useMemo(() => ({ mode, theme, setMode, brandColor, setBrandColor }), [mode, theme, setMode, brandColor, setBrandColor]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// { mode: 'light' | 'dark' | 'system', theme: 'light' | 'dark', setMode, brandColor: '#rrggbb', setBrandColor }. Fora do
// ThemeProvider (testes), tema claro e a cor padrão
export const useTheme = () => useContext(ThemeContext);
